import type { Direction, LinkMode, SiteDetail, UploadKind } from '@tabel/shared/schemas/sites';
import { COMMON, RISK_TEXTS, STEP_BODY, type StepAction } from '@tabel/shared/sites/step-texts';
import { useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useToast } from '../../../components/ErrorToast';
import { ApiError } from '../../../lib/errors';
import { useRole } from '../../../lib/role';
import * as api from '../api';
import { sitesKeys } from '../api';
import { SCREEN, copyText } from '../parts';
import { DANGER, FORM, SITE } from '../texts';

/*
 * Действия страницы площадки (007 T040, T041, T053, T055; contracts/http-api.md, «Страница площадки»). Каждое
 * изменение уходит с `revision` последнего ответа сервера: устаревшая ревизия — `409 SITE_REVISION_CONFLICT`, тогда
 * тост и площадка перечитывается (две вкладки, два администратора). После изменения площадка перечитывается; ответ
 * `PUT …/link` — страница площадки, она сразу на экране. Ошибки — тостом с кодом ошибки (requestId); сбой сервера тост
 * сам пишет в журнал ошибок интерфейса. Пароль SmartPSS живёт только в состоянии страницы, пока шаг 4 не выполнен.
 */

/** Окно страницы: «Продолжить с риском», подтверждения «Опасной зоны», текст для копирования вручную. */
export type SiteDialog =
  | { kind: 'risk'; step: number }
  | { kind: 'reissue'; what: 'password' | 'vpn_key' }
  | { kind: 'link'; mode: LinkMode }
  | { kind: 'disable' }
  | { kind: 'retire' }
  | { kind: 'copy'; title: string; text: string };

export interface PasswordState {
  value: string | null;
  shown: boolean;
}

export function useSiteActions(code: string, detail: SiteDetail | undefined, openStep: (n: number) => void) {
  const qc = useQueryClient();
  const { role } = useRole();
  const { showError, showInfo } = useToast();
  const key = sitesKeys.site(role, code);
  const latest = useRef(detail);
  latest.current = detail;
  const busyRef = useRef<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, setDialog] = useState<SiteDialog | null>(null);
  const [password, setPassword] = useState<PasswordState>({ value: null, shown: false });
  const passwordRef = useRef(password);
  passwordRef.current = password;

  // Шаг 4 выполнен (или выдан новый пароль) — прежний пароль со страницы стирается.
  const available = detail?.smartpss.passwordAvailable ?? false;
  useEffect(() => {
    if (!available) setPassword({ value: null, shown: false });
  }, [available]);

  const revision = () => latest.current?.revision ?? 1;
  const name = () => latest.current?.site.name ?? code;
  const refresh = () => void qc.invalidateQueries({ queryKey: key });
  const refreshList = () => void qc.invalidateQueries({ queryKey: sitesKeys.list(role) });

  /**
   * Одно действие за раз: второе нажатие, пока идёт запрос, ничего не делает. Итог — выполнено ли действие: защита
   * несохранённого уводит со страницы только после сохранения (T047).
   */
  async function run<T>(action: string, fn: () => Promise<T>, ok?: (r: T) => void): Promise<boolean> {
    if (busyRef.current) return false;
    busyRef.current = action;
    setBusy(action);
    try {
      const r = await fn();
      ok?.(r);
      return true;
    } catch (err) {
      // Площадку изменили в другом окне или шаги не готовы — на экране устаревшее состояние: перечитать.
      if (
        err instanceof ApiError &&
        (err.code === 'SITE_REVISION_CONFLICT' || err.code === 'STEPS_NOT_READY')
      )
        refresh();
      showError(err, { screen: SCREEN, action });
      return false;
    } finally {
      busyRef.current = null;
      setBusy(null);
    }
  }

  /** Скопировать; буфер обмена недоступен — окно с текстом, выделенным для копирования вручную. */
  async function copy(title: string, text: string) {
    if (await copyText(text)) showInfo(COMMON.copied, { duration: 2000 });
    else setDialog({ kind: 'copy', title, text });
  }

  const secret = async () => passwordRef.current.value ?? (await api.siteSecret(code));

  const doLink = (mode: LinkMode) =>
    run(
      'link',
      () => api.putLink(code, { revision: revision(), mode }),
      (d) => {
        setDialog(null);
        qc.setQueryData(key, d);
        refreshList();
      },
    );

  const enableSite = () =>
    run(
      'enable',
      () => api.enable(code),
      () => {
        refresh();
        refreshList();
        showInfo(DANGER.enable.done(name()));
      },
    );

  const changeLink = (mode: LinkMode) => {
    const d = latest.current;
    if (!d || d.site.linkMode === mode) return;
    if (d.steps.find((s) => s.step === 4)?.status === 'ok') setDialog({ kind: 'link', mode });
    else void doLink(mode);
  };
  const download = (kind: 'wireguard' | 'amnezia') =>
    run(`vpn-config-${kind}`, () => api.downloadVpnConfig(code, kind));
  const copyPassword = () =>
    run('secret-copy', async () => {
      const value = await secret();
      setPassword((p) => ({ ...p, value }));
      await copy(STEP_BODY[4].values.password, value);
    });
  const recheck = () =>
    run(
      'recheck',
      () => api.recheck(code),
      () => {
        showInfo(COMMON.recheckStarted);
        refresh();
      },
    );
  const resetPass = () => run('control-reset', () => api.controlPass(code, { kind: 'reset' }), refresh);

  /** Действие-ссылка подсказки шага (T041): что делать — по `kind` из `step-texts`. */
  const hint = (a: StepAction, step: number): void => {
    switch (a.kind) {
      case 'copyPassword':
        void copyPassword();
        break;
      case 'copyValue':
        void copy(a.label, a.value);
        break;
      case 'recheck':
        void recheck();
        break;
      case 'allowAddress':
        void run(
          'allow-address',
          () => api.allowAddress(code, { revision: revision(), address: a.address }),
          () => {
            showInfo(SITE.addressAllowed(a.address));
            refresh();
          },
        );
        break;
      case 'gotoStep':
        openStep(a.step);
        break;
      case 'switchToDirect':
        changeLink('direct');
        break;
      case 'openAmnezia':
        // Внешняя ссылка: её открывает браузер (`StepHints`).
        break;
      case 'downloadAmneziaConfig':
        void download('amnezia');
        break;
      case 'confirmVpnActive':
        void run(
          'vpn-active',
          () => api.putAnswers(code, { revision: revision(), step: 3, answers: { vpnActive: true } }),
          refresh,
        );
        break;
      case 'retryPrepare':
        void run('retry-prepare', () => api.recheck(code), refresh);
        break;
      case 'restartControl':
        void resetPass();
        break;
      case 'acceptRisk':
        setDialog({ kind: 'risk', step });
        break;
    }
  };

  const act = {
    hint,
    /** Шаг 2: ответы со слов администратора. */
    saveStep2: (answers: { version: string; terminals: number; allDay: boolean; keepDays: number }) =>
      run(
        'answers-2',
        () => api.putAnswers(code, { revision: revision(), step: 2, answers }),
        () => {
          showInfo(FORM.answersSaved);
          refresh();
        },
      ),
    /** Шаг 7: «Подтвердить вручную» — два утра без строк выгрузки, расписание в SmartPSS стоит. */
    confirmExtract: () =>
      run('extract-confirm', () => api.confirmExtract(code, { revision: revision() }), refresh),
    /** Шаг 7: «Готово» — настройки SmartPSS сделаны, ждём выгрузку. */
    saveStep7: (times: string[]) =>
      run(
        'answers-7',
        () => api.putAnswers(code, { revision: revision(), step: 7, answers: { times } }),
        refresh,
      ),
    /**
     * Способ связи. Связь уже работает (шаг 4 выполнен) — сначала подтверждение: площадка вернётся к шагам 3 и 4
     * (FR-061). Иначе — сразу.
     */
    changeLink,
    confirmLink: (mode: LinkMode) => void doLink(mode),
    download,
    showPassword: () =>
      run('secret-show', async () => {
        const value = await secret();
        setPassword({ value, shown: true });
      }),
    hidePassword: () => setPassword((p) => ({ ...p, shown: false })),
    copyPassword,
    copyValue: (title: string, value: string) => void copy(title, value),
    setDirection: (ip: string, direction: Direction) =>
      run(
        'terminal-direction',
        () => api.putTerminal(code, ip, { revision: revision(), direction }),
        refresh,
      ),
    confirmDirections: () =>
      run(
        'terminal-directions-confirm',
        () =>
          api.putAnswers(code, {
            revision: revision(),
            step: 5,
            answers: { manualDirectionsConfirmed: true },
          }),
        refresh,
      ),
    pass: (kind: 'in' | 'out') =>
      run(
        'control-pass',
        () => api.controlPass(code, { kind, pressedAt: new Date().toISOString() }),
        refresh,
      ),
    resetPass,
    skipControl: () => run('control-skip', () => api.controlPass(code, { kind: 'skip' }), refresh),
    upload: (kind: UploadKind, file: File) =>
      run(
        `upload-${kind}`,
        () => api.upload(code, kind, file),
        (u) => {
          showInfo(FORM.uploaded(u.fileName));
          refresh();
        },
      ),
    launch: (startDate: string, rulesSource: string, rulesAdmins: readonly number[]) =>
      run(
        'launch',
        () =>
          api.launch(code, { revision: revision(), startDate, rulesSource, rulesAdmins: [...rulesAdmins] }),
        () => {
          showInfo(SITE.launched(name()));
          refresh();
          refreshList();
          // Новые роли «Администратор правил» — в переключателе объекта.
          void qc.invalidateQueries({ queryKey: ['me'] });
        },
      ),
    /** «Подготовить правила» (FR-004a): образец и кто ведёт правила — до запуска. */
    prepareRules: (rulesSource: string, rulesAdmins: readonly number[]) =>
      run(
        'rules-prep',
        () => api.prepareRules(code, { revision: revision(), rulesSource, rulesAdmins: [...rulesAdmins] }),
        () => {
          showInfo(STEP_BODY[10].preparedToast);
          refresh();
          // Кто уже ведёт правила площадки — в списке людей шага 10; новые роли — в переключателе объекта.
          void qc.invalidateQueries({ queryKey: ['sites', role, 'rule-people', code] });
          void qc.invalidateQueries({ queryKey: ['me'] });
        },
      ),
    recheck,
    acceptRisk: (step: number, reason: string) =>
      run(
        'risk',
        () => api.acceptRisk(code, { revision: revision(), step, reason }),
        () => {
          setDialog(null);
          showInfo(RISK_TEXTS.toast);
          refresh();
        },
      ),
    reissue: (what: 'password' | 'vpn_key') =>
      run(
        `reissue-${what}`,
        () => api.reissue(code, { revision: revision(), kind: what }),
        () => {
          setDialog(null);
          if (what === 'password') setPassword({ value: null, shown: false });
          showInfo(what === 'password' ? DANGER.password.done : DANGER.vpnKey.done);
          refresh();
          openStep(what === 'password' ? 4 : 3);
        },
      ),
    disable: (confirmName: string) =>
      run(
        'disable',
        () => api.disable(code, { revision: revision(), confirmName }),
        () => {
          setDialog(null);
          refresh();
          refreshList();
          showInfo(DANGER.disable.done(name()), {
            action: { label: DANGER.disable.undo, onClick: () => void enableSite() },
          });
        },
      ),
    enable: () => void enableSite(),
    /** Прежняя учётная запись первой площадки (US5, R17): ответ — страница площадки, она сразу на экране. */
    retireLegacy: (confirmName: string) =>
      run(
        'retire-legacy',
        () => api.retireLegacy(code, { revision: revision(), confirmName }),
        (d) => {
          setDialog(null);
          qc.setQueryData(key, d);
          showInfo(DANGER.legacy.done);
        },
      ),
    support: () =>
      run('support', async () => {
        await copy(COMMON.support, await api.supportText(code));
      }),
  };

  return { act, busy, dialog, setDialog, password };
}

export type SiteActions = ReturnType<typeof useSiteActions>['act'];

/** Что знают тела шагов: площадка, действия, идёт ли запрос, пароль. */
export interface SiteCtx {
  detail: SiteDetail;
  act: SiteActions;
  busy: string | null;
  password: PasswordState;
  setDialog: (d: SiteDialog | null) => void;
}

const Ctx = createContext<SiteCtx | null>(null);
export const SiteCtxProvider = Ctx.Provider;

export function useSite(): SiteCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useSite: нет SiteCtxProvider');
  return v;
}
